<script setup lang="ts">
import { ElMessage } from 'element-plus'
import { computed, onMounted, ref, getCurrentInstance, nextTick, watch, defineAsyncComponent } from 'vue'
import { useWindowSize } from '@vueuse/core'
import { renderMessage, isMobile } from '@/utils/util'

const { t } = useI18n()
const { width } = useWindowSize()

const VueCropperComponent = defineAsyncComponent(() =>
  import('vue-cropper').then((m) => {
    if (typeof window !== 'undefined') {
      import('vue-cropper/dist/index.css')
    }
    // @ts-ignore vue-cropper 类型定义不完整，运行时存在 VueCropper 命名导出
    return m.VueCropper || (m as any).default?.VueCropper || m.default
  })
)

const props = defineProps({
  path: {
    type: String,
    required: false,
    default: ''
  },
  modelValue: {
    type: Boolean,
    required: false,
    default: true
  },
  // fixed 表示只能截取1:1大小的图片
  fixed: {
    type: Boolean,
    default: false
  },
  cropSize: {
    type: String,
    default: ''
  },
  threeToFour: {
    type: Boolean,
    default: false
  },
  highQuality: {
    type: Boolean,
    default: false
  }
})

const enlargeNumber = computed(() => {
  return props.highQuality ? 5 : 3
})

// 截图尺寸配置
const CROP_SIZE_CONFIGS: Record<
  string,
  {
    mobile: { width: number; height: number; enlarge: number }
    pc: { width: number; height: number; enlarge: number }
  }
> = {
  'A4 / A3': {
    mobile: { width: 280, height: 396, enlarge: enlargeNumber.value },
    pc: { width: 280, height: 396, enlarge: enlargeNumber.value }
  },
  '12寸 / 24寸': {
    mobile: { width: 375, height: 450, enlarge: enlargeNumber.value },
    pc: { width: 417, height: 500, enlarge: enlargeNumber.value }
  },
  '16寸 / 8寸 / 4寸': {
    mobile: { width: 336, height: 449, enlarge: enlargeNumber.value },
    pc: { width: 375, height: 500, enlarge: enlargeNumber.value }
  },
  '1寸 / 2寸 / 7寸': {
    mobile: { width: 300, height: 420, enlarge: enlargeNumber.value },
    pc: { width: 350, height: 490, enlarge: enlargeNumber.value }
  },
  大1寸: {
    mobile: { width: 308, height: 448, enlarge: enlargeNumber.value },
    pc: { width: 344, height: 500, enlarge: enlargeNumber.value }
  },
  '5寸': {
    mobile: { width: 315, height: 450, enlarge: enlargeNumber.value },
    pc: { width: 350, height: 500, enlarge: enlargeNumber.value }
  },
  '6寸': {
    mobile: { width: 300, height: 450, enlarge: enlargeNumber.value },
    pc: { width: 333, height: 500, enlarge: enlargeNumber.value }
  },
  '10寸 / 20寸': {
    mobile: { width: 360, height: 450, enlarge: enlargeNumber.value },
    pc: { width: 400, height: 500, enlarge: enlargeNumber.value }
  },
  '18寸': {
    mobile: { width: 350, height: 450, enlarge: enlargeNumber.value },
    pc: { width: 389, height: 500, enlarge: enlargeNumber.value }
  }
}

const currentCropConfig = computed(() => {
  if (props.fixed) {
    return {
      width: 350,
      height: 350,
      enlarge: enlargeNumber.value
    }
  }

  let configKey = props.cropSize
  if (!configKey || !CROP_SIZE_CONFIGS[configKey]) {
    // 兼容旧的 props
    if (props.threeToFour || route.query.threeToFour == 'true') {
      configKey = '16寸 / 8寸 / 4寸'
    } else {
      configKey = '16寸 / 8寸 / 4寸'
    }
  }

  const config = CROP_SIZE_CONFIGS[configKey]

  const targetConfig = isMobile.value ? config.mobile : config.pc
  return {
    ...targetConfig,
    enlarge: enlargeNumber.value
  }
})

const fixedNumber = computed(() => {
  const config = currentCropConfig.value
  return [config.width, config.height]
})

// 路由相关
const route = useRoute()
const router = useRouter()

// 判断是否为页面模式
const isPageMode = computed(() => {
  return route.name === 'crop'
})

// 获取图片路径，优先使用路由参数
const getImagePath = () => {
  if (isPageMode.value && route.query.path) {
    return route.query.path as string
  }
  return props.path
}

// https://github.com/xyxiao001/vue-cropper
const option = ref({
  img: getImagePath() ? new URL(getImagePath()!, import.meta.url).href : '',
  size: 1, // 裁剪生成图片的质量 - 0.1 ~ 1
  full: false, // 是否输出原图比例的截图
  outputType: 'png', // 裁剪生成图片的格式
  canMove: true, // 上传图片是否可以移动
  fixedBox: true, // 固定截图框大小
  original: false, // 上传图片按照原始比例渲染
  canMoveBox: false, // 截图框能否拖动
  autoCrop: true, // 是否默认生成截图框
  // 只有自动截图开启 宽度高度才生效
  autoCropWidth: currentCropConfig.value.width, // 默认生成截图框宽度
  autoCropHeight: currentCropConfig.value.height, // 默认生成截图框高度
  centerBox: true, // 截图框是否被限制在图片里面
  high: true, // 是否按照设备的dpr 输出等比例图片
  max: 99999, // 限制图片最大宽度和高度
  enlarge: currentCropConfig.value.enlarge // 图片根据截图框输出比例倍数
})

watch([() => props.cropSize, () => props.fixed], () => {
  option.value.autoCropWidth = currentCropConfig.value.width
  option.value.autoCropHeight = currentCropConfig.value.height
  option.value.enlarge = currentCropConfig.value.enlarge
})

watch(
  () => props.highQuality,
  () => {
    option.value.enlarge = currentCropConfig.value.enlarge
  }
)

watch(
  () => props.path,
  () => {
    if (!isPageMode.value) {
      console.log('props.path:', props.path)
      option.value.img = props.path ? new URL(props.path!, import.meta.url).href : ''
    }
  }
)

const photoMode = computed(() => {
  return isMobile.value ? '100%' : '50%'
})

onMounted(() => {
  // 如果是页面模式，从路由参数获取path
  if (isPageMode.value && route.query.path) {
    option.value.img = new URL(route.query.path as string, import.meta.url).href
    console.log('页面模式 - option.value.img:', route.query.path)
  } else {
    console.log('组件模式 - option.value.img:', props.path)
  }

  // 初始加载完成后，延迟将截图框外变黑
  moveTimer = setTimeout(() => {
    isOutsideHidden.value = true
  }, 1000)
})

// cropper的vue实例
// @ts-ignore
const vueCropperRef = ref<VueCropper>()
// 是否可见
const pageVisibe = computed(() => {
  if (isPageMode.value) {
    return true
  }
  if (props.modelValue) {
    nextTick(() => {
      getCurrentInstance()?.proxy?.$forceUpdate()
      // changeRatio( 3/4 )
      // 解决了初始化cropper变小不重绘的问题
      // cropperOptions.viewMode = 0
    })
  }
  return props.modelValue
})

const emit = defineEmits(['onCropped', 'update:modelValue'])

// 保存截图图像
const saveCropData = () => {
  vueCropperRef.value?.getCropBlob((data: any) => {
    console.log('data:', data)

    if (isPageMode.value) {
      // 页面模式：将裁剪数据转为base64，存储到localStorage
      const reader = new FileReader()
      reader.readAsDataURL(data)
      reader.onloadend = () => {
        const base64data = reader.result
        if (import.meta.client) {
          localStorage.setItem('croppedImage', base64data as string)
        }

        // 返回到来源页面
        if (route.query.returnTo) {
          router.replace({
            path: route.query.returnTo as string,
            query: {
              cropped: 'true',
              width: option.value.autoCropWidth,
              height: option.value.autoCropHeight
            }
          })
        } else {
          router.back()
        }
      }
    } else {
      // 组件模式：通过emit传递数据
      console.log('enlargeNumber:', enlargeNumber.value)
      emit('onCropped', data, option.value.autoCropWidth, option.value.autoCropHeight)
      emit('update:modelValue', false)
    }

    reset()
  })

  // 下载
  // croppedCanvas?.toBlob((blob: Blob | null) => {
  //   if (blob) {
  //     const url = URL.createObjectURL(blob)
  //     const a = document.createElement('a')
  //     a.href = url
  //     a.download = 'cropped.png'
  //     a.click()
  //     URL.revokeObjectURL(url)
  //   }
  // })
}

// 重置初始设置
function reset() {
  // changeRatio('3:4')
  option.value.autoCropWidth = currentCropConfig.value.width
  option.value.autoCropHeight = currentCropConfig.value.height
  option.value.enlarge = currentCropConfig.value.enlarge
}

function hide() {
  if (isPageMode.value) {
    // 页面模式：返回上一页
    router.back()
  } else {
    // 组件模式：触发事件
    emit('update:modelValue', false)
  }
  reset()
}

// 改变截图比例
// function changeRatio(ratio: string) {
//   aspectRatio.value = RATIO_CONFIG[ratio].ratio
//   option.value.autoCropWidth = RATIO_CONFIG[ratio].maxWidth
//   option.value.autoCropHeight = RATIO_CONFIG[ratio].maxHeight
// }

// file select input
const fileInputRef = ref()
const onFileSelect = (event: any) => {
  const files = event.target.files

  if (files && files.length) {
    const file = files[0]
    if (/^image\/\w+/.test(file.type)) {
      option.value.img = URL.createObjectURL(file)
      fileInputRef.value.value = null
      // 重置变黑状态，新图片加载时先显示
      isOutsideHidden.value = false
      if (moveTimer) clearTimeout(moveTimer)
      moveTimer = setTimeout(() => {
        isOutsideHidden.value = true
      }, 500)
    } else {
      ElMessage.warning({
        message: renderMessage(t('exception.file.wrong_type'))
      })
    }
  }
}

const isOutsideHidden = ref(false)
let moveTimer: any = null

const onCropperStateChange = (event: any) => {
  // 图片移动/缩放/变化时，显示截图框外的图片（变暗效果），方便用户对齐
  isOutsideHidden.value = false

  // 清除之前的定时器
  if (moveTimer) {
    clearTimeout(moveTimer)
  }

  // 停止变化 0.5 秒后，将截图框外完全变黑
  moveTimer = setTimeout(() => {
    isOutsideHidden.value = true
  }, 700)
}

watch(
  () => props.modelValue,
  (newVal) => {
    isOutsideHidden.value = false
    if (newVal && props.path) {
      nextTick(() => {
        vueCropperRef.value?.refresh()
      })
    }
    moveTimer = setTimeout(() => {
      isOutsideHidden.value = true
    }, 1000)
  }
)
</script>
<template>
  <div v-show="pageVisibe" h-full w-full flex flex-col bg-transparent>
    <div class="h-full w-full">
      <VueCropperComponent
        h-full
        w-full
        ref="vueCropperRef"
        :img="option.img"
        :output-size="option.size"
        :output-type="option.outputType"
        :info="false"
        :full="option.full"
        :fixed="true"
        :fixed-number="fixedNumber"
        :can-move="option.canMove"
        :can-move-box="option.canMoveBox"
        :fixed-box="option.fixedBox"
        :original="option.original"
        :auto-crop="option.autoCrop"
        :auto-crop-width="option.autoCropWidth"
        :auto-crop-height="option.autoCropHeight"
        :center-box="option.centerBox"
        :high="option.high"
        :enlarge="option.enlarge"
        :mode="photoMode"
        :max-img-size="option.max"
        bg-black
        class="cropper"
        :class="{ 'hide-outside': isOutsideHidden }"
        @realTime="onCropperStateChange"
        @imgMoving="onCropperStateChange" />
    </div>
    <div flex flex-row justify-between items-center absolute bottom-5 left-0 right-0 z-10 md:left-80 md:right-80>
      <div cursor-pointer text-xl @click="hide" flex-1 text-inverted text-center p-1 hover:text-gray-500>
        {{ $t('action.cancel') }}
      </div>
      <div
        class="border-base text-inverted"
        flex-r-center
        rounded-b
        text-center
        p-1
        @click="vueCropperRef?.rotateLeft()"
        cursor-pointer
        flex-1>
        <span i-carbon:rotate-counterclockwise w-6 h-6 hover:bg-gray-500></span>
      </div>
      <div flex-1 text-xl @click="saveCropData" cursor-pointer text-inverted text-center p-1 hover:text-gray-500>
        {{ $t('action.finished') }}
      </div>
    </div>
    <!-- <div v-if="!props.fixed && !threeToFour" flex flex-row justify-center py-8>
      <div flex flex-col items-center>
        <div class="border-base bg-gray-600 text-inverted hover:bg-gray-500"
          :class="{ 'border-selected': aspectRatio == RATIO_CONFIG['3:4'].ratio }" h-16 w-16 flex-r-center rounded-2
          @click="changeRatio('3:4')">
          <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="5" y="1" width="20" height="28" rx="1" stroke="#EDEDED" stroke-dasharray="2 2" />
          </svg>
        </div>
        <span class="text-inverted" mt-1>3:4</span>
      </div>
      <div mx-12 flex flex-col items-center md:mx-16>
        <div class="border-base bg-gray-600 text-inverted hover:bg-gray-500" h-16 w-16 flex-r-center rounded-2
          :class="{ 'border-selected': aspectRatio == RATIO_CONFIG['4:3'].ratio }" @click="changeRatio('4:3')">
          <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="5" width="28" height="21" rx="1" stroke="#EDEDED" stroke-dasharray="2 2" />
          </svg>
        </div>
        <span class="text-inverted" mt-1>4:3</span>
      </div>
      <div flex flex-col items-center>
        <div class="border-base bg-gray-600 text-inverted hover:bg-gray-500" h-16 w-16 flex-r-center rounded-2
          :class="{ 'border-selected': aspectRatio == RATIO_CONFIG['1:1'].ratio }" @click="changeRatio('1:1')">
          <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="24" height="24" rx="1" stroke="#EDEDED" stroke-dasharray="2 2" />
          </svg>
        </div>
        <span class="text-inverted" mt-1>1:1</span>
      </div>
    </div> -->
  </div>
</template>
<style lang="scss" scoped>
.border-selected {
  border: 1px solid var(--c-border);
}

.cropper {
  background-image: none;
  background-color: black !important;
  background-image: none !important;

  &.hide-outside {
    // 隐藏截图框外的图片（底图）
    // .cropper-box-canvas 包含了底图
    :deep(.cropper-box-canvas) {
      opacity: 0.5;
      transition: opacity 0.5s ease;
    }
  }
}
:deep(.cropper-bg) {
  background-image: none !important;
  background-color: #000000 !important;
}
</style>
